import { BOARD_SIZE, cellIndex } from '../game/board';
import { BLACK_PROBABILITY } from '../game/stones';
import type { Game } from '../game/game';
import { gridToCell, sectionAxes, type Face } from './crossSection';

/** 黒確率 p を「白→黒」の補間色（CSS）にする */
function probabilityColor(p: number): string {
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * p);
  const v = lerp(235, 25);
  return `rgb(${v}, ${v}, ${v})`;
}

/**
 * 選択中の断面を 10×10 の 2D グリッドとして表示するコンポーネント。
 * 盤面内部のマスに確実な石を置くための主たる操作手段。
 *
 * マスクリックは元の 3D 座標に変換して onCellClick へ渡し、
 * 実際の配置は呼び出し側（App）が既存のゲームロジック経由で行う。
 * このグリッドは盤面データを書き換えない（読み取り専用）。
 */
export class SectionGrid {
  onCellClick: ((x: number, y: number, z: number) => void) | null = null;
  onCellHover: ((x: number, y: number, z: number) => void) | null = null;
  onGridLeave: (() => void) | null = null;

  private readonly container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /** 指定の面・深さの断面をゲーム状態からレンダリングする */
  render(face: Face, depth: number, game: Game, placeable: boolean): void {
    const { rowAxis, colAxis } = sectionAxes(face);
    this.container.textContent = '';

    const caption = document.createElement('div');
    caption.className = 'section-grid-caption';
    caption.textContent = `行: ${rowAxis} 軸 / 列: ${colAxis} 軸（各ラベルは座標値）`;
    this.container.append(caption);

    const grid = document.createElement('div');
    grid.className = 'section-grid';
    grid.style.gridTemplateColumns = `auto repeat(${BOARD_SIZE}, 1fr)`;
    grid.style.gridTemplateRows = `auto repeat(${BOARD_SIZE}, 1fr)`;

    // 角のラベル
    const corner = document.createElement('span');
    corner.className = 'grid-label corner';
    grid.append(corner);

    // 列ラベル（colAxis の座標値）
    for (let col = 0; col < BOARD_SIZE; col++) {
      const label = document.createElement('span');
      label.className = 'grid-label';
      label.textContent = String(gridToCell(face, depth, 0, col)[colAxis]);
      grid.append(label);
    }

    // 行ラベル（rowAxis の座標値）と 10×10 のマス
    for (let row = 0; row < BOARD_SIZE; row++) {
      const rowLabel = document.createElement('span');
      rowLabel.className = 'grid-label';
      rowLabel.textContent = String(gridToCell(face, depth, row, 0)[rowAxis]);
      grid.append(rowLabel);

      for (let col = 0; col < BOARD_SIZE; col++) {
        const { x, y, z } = gridToCell(face, depth, row, col);
        const stone = game.state.board[cellIndex(x, y, z)];
        const cell = document.createElement('button');
        cell.className = 'section-cell';
        cell.title = `x=${x}, y=${y}, z=${z}`;

        if (stone !== null) {
          if (stone.observedColor === 'black') {
            cell.classList.add('black');
          } else if (stone.observedColor === 'white') {
            cell.classList.add('white');
          } else {
            cell.classList.add('quantum');
            cell.style.background = probabilityColor(BLACK_PROBABILITY[stone.type]);
          }
          cell.textContent = String(stone.type);
          cell.disabled = true; // 占有済みマスには新しい石を置けない
        } else {
          cell.classList.add('empty');
          cell.disabled = !placeable;
        }

        cell.addEventListener('click', () => {
          if (this.onCellClick) this.onCellClick(x, y, z);
        });
        cell.addEventListener('pointerenter', () => {
          if (this.onCellHover) this.onCellHover(x, y, z);
        });
        grid.append(cell);
      }
    }

    grid.addEventListener('pointerleave', () => {
      if (this.onGridLeave) this.onGridLeave();
    });
    this.container.append(grid);
  }

  /** グリッドを隠す（全体表示モード） */
  hide(): void {
    this.container.textContent = '';
  }
}
